import { interpolate, useCurrentFrame, useVideoConfig, Easing } from 'remotion';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { createBadgePath, createCheckmarkPath } from './utils';

interface VerifiedIconProps {
  size?: number;
  strokeWidth?: number;
  /** Frame at which the animation starts (relative to the Sequence) */
  startFrame?: number;
  /** Duration in frames for drawing the badge outline */
  badgeDrawDuration?: number;
  /** Duration in frames for drawing the checkmark */
  checkmarkDrawDuration?: number;
  /** Delay in frames between badge completion and checkmark start */
  delayBetween?: number;
}

export const VerifiedIcon = ({
  size = 280,
  strokeWidth = 20,
  startFrame = 0,
  badgeDrawDuration = 60,
  checkmarkDrawDuration = 25,
  delayBetween = 0,
}: VerifiedIconProps) => {
  const frame = useCurrentFrame();
  const { rawColors } = employeeYearInReviewTheme;

  // Animation timing
  const animationFrame = frame - startFrame;
  // Start checkmark earlier for seamless transition (25% overlap with badge animation)
  const overlapFrames = Math.min(25, badgeDrawDuration * 0.25);
  const checkmarkStartFrame = badgeDrawDuration - overlapFrames + delayBetween;

  // Badge path from template SVG
  const badgePath = createBadgePath(size);
  // Approximate path length for animation (template is ~350 at 96px, scale proportionally)
  const badgePathLength = 350 * (size / 96);

  // Checkmark path from template SVG
  const checkmarkPath = createCheckmarkPath(size);
  // Checkmark length (calculated: ~41 at 96px, use higher value to prevent stroke repetition)
  const checkmarkPathLength = 50 * (size / 96);

  // Badge animation progress (0 to 1)
  const badgeProgress = interpolate(
    animationFrame,
    [0, badgeDrawDuration],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.inOut(Easing.quad),
    },
  );

  // Checkmark animation progress (0 to 1)
  const checkmarkProgress = interpolate(
    animationFrame,
    [checkmarkStartFrame, checkmarkStartFrame + checkmarkDrawDuration],
    [0, 1],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.inOut(Easing.quad),
    },
  );

  // Calculate stroke-dashoffset for drawing animation
  const badgeDashOffset = badgePathLength * (1 - badgeProgress);
  const checkmarkDashOffset = checkmarkPathLength * (1 - checkmarkProgress);

  // Hide paths completely when animation hasn't started (fixes dot issue)
  const badgeOpacity = animationFrame < 0 ? 0 : 1;
  const checkmarkOpacity = animationFrame < checkmarkStartFrame ? 0 : 1;

  // Continuous rotation after badge drawing completes
  const { fps } = useVideoConfig();
  const rotationStartFrame = badgeDrawDuration;
  const rotationSpeed = 360 / (fps * 8); // Full rotation every 8 seconds
  const badgeRotation =
    animationFrame > rotationStartFrame
      ? (animationFrame - rotationStartFrame) * rotationSpeed
      : 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Badge outline (wavy circle) */}
      <path
        d={badgePath}
        stroke={rawColors.textTertiary}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        style={{
          strokeDasharray: badgePathLength,
          strokeDashoffset: badgeDashOffset,
          opacity: badgeOpacity,
          transform: `rotate(${badgeRotation}deg)`,
          transformOrigin: 'center center',
        }}
      />

      {/* Checkmark */}
      <path
        d={checkmarkPath}
        stroke={rawColors.textTertiary}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        style={{
          strokeDasharray: checkmarkPathLength,
          strokeDashoffset: checkmarkDashOffset,
          opacity: checkmarkOpacity,
        }}
      />
    </svg>
  );
};
